import { type CustomTooltipProps } from "ag-grid-react"

import { type ExpectedBasis } from "~/components/demand-2/shared/useDemand2PerformanceByCell"
import { StatusColor } from "~/constants/status"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"

import HeaderTooltip from "./HeaderTooltip"
import TooltipMessageCard from "./TooltipMessageCard"

/** The two sums behind a "Sales vs expected" cell and where the expected side came from. */
export type VsExpectedCalc = {
  tooltipType: "VsExpected"
  /** Forecast-covered weeks only. */
  sales: number
  expected: number
  /** Distinct calendar weeks across the row. */
  weeks: number
  /** Two when a group mixes published and historic variants. */
  bases: ExpectedBasis[]
}

const BASIS_LABEL: Record<ExpectedBasis, string> = {
  plan: "Published demand",
  model: "Historic demand",
}

const basisBadge = (bases: ExpectedBasis[]) => {
  if (bases.length === 0) return undefined
  const text = bases.length === 1 ? BASIS_LABEL[bases[0]!] : "Published and historic demand"
  const color =
    bases.length === 1 && bases[0] === "plan" ? StatusColor.Information : StatusColor.Neutral
  return { text, color }
}

/** Enough decimals that a tiny expected does not read as 0. */
const units = (n: number) => {
  if (n >= 100 || Number.isInteger(n)) return formatNumber(n, 0)
  return formatNumber(n, n >= 1 ? 1 : 3)
}

const VsExpectedTooltip = (props: CustomTooltipProps) => {
  if (props.location === "header") return <HeaderTooltip {...props} />
  const calc = props.value as VsExpectedCalc | null | undefined
  if (!calc || calc.tooltipType !== "VsExpected") return null
  const pct = calc.expected > 0 ? (calc.sales / calc.expected) * 100 : null
  return (
    <TooltipMessageCard badge={basisBadge(calc.bases)} className="max-w-80">
      <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 w-full tabular-nums">
        <span className="text-fg-muted">{formatName("sale", true, true)} in covered weeks</span>
        <span className="text-right">{units(calc.sales)}</span>
        <span className="text-fg-muted">Expected demand</span>
        <span className="text-right">{units(calc.expected)}</span>
        <span className="text-fg-muted">Covered weeks</span>
        <span className="text-right">{formatNumber(calc.weeks, 0)}</span>
      </div>
      <div className="border-t border-base pt-1 mt-1 w-full tabular-nums">
        {pct == null && calc.sales > 0
          ? `Expected demand is 0, so ${units(calc.sales)} ${formatName("sale", calc.sales !== 1)} can't be shown as a percentage of it.`
          : `${units(calc.sales)} ÷ ${units(calc.expected)} × 100 = ${pct == null ? "N/A" : `${formatNumber(pct, 0)} %`}`}
      </div>
    </TooltipMessageCard>
  )
}

export default VsExpectedTooltip
